import type { ReactNode } from 'react';
import type { AgentIntegrationOverviewItem } from 'services/integration-service';
import gdriveIcon from 'assets/images/gdrive.svg';
import sharepointIcon from 'assets/images/sharepoint.svg';

export type ProviderVisualConfig = {
  icon?: string;
  accentClassName: string;
  glyph?: ReactNode;
};

const PROVIDER_ALIASES: Record<string, string> = {
  google: 'google-drive',
  googledrive: 'google-drive',
  google_drive: 'google-drive',
  sharepoint: 'microsoft-sharepoint',
  microsoft: 'microsoft-sharepoint',
  microsoft_sharepoint: 'microsoft-sharepoint',
  'microsoft-sharepoint': 'microsoft-sharepoint',
  teams: 'teams',
  'microsoft-teams': 'teams',
  microsoft_teams: 'teams',
};

const PROVIDER_VISUALS: Record<string, ProviderVisualConfig> = {
  telegram: {
    icon: '/assets/images/agents/telegram-brand.svg',
    accentClassName: 'from-sky-500/15 via-blue-500/10 to-cyan-500/15',
  },
  whatsapp: {
    icon: '/assets/images/agents/whatsapp-brand.svg',
    accentClassName: 'from-emerald-500/15 via-green-500/10 to-teal-500/15',
  },
  teams: {
    icon: '/assets/images/agents/teams.png',
    accentClassName: 'from-indigo-500/15 via-blue-500/10 to-violet-500/15',
  },
  'google-drive': {
    icon: gdriveIcon,
    accentClassName: 'from-blue-500/15 via-sky-500/10 to-emerald-500/15',
  },
  'microsoft-sharepoint': {
    icon: sharepointIcon,
    accentClassName: 'from-emerald-500/15 via-teal-500/10 to-cyan-500/15',
  },
};

const CONNECTED_STATUSES = new Set(['active', 'connected']);

const slugify = (value?: string) =>
  (value ?? '')
    .toLowerCase()
    .trim()
    .replace(/[_\s]+/g, '-');

export const isIntegrationConnected = (status?: string) => {
  const normalized = (status ?? '').trim().toLowerCase();
  return CONNECTED_STATUSES.has(normalized);
};

export const APP_INTEGRATION_ACTIONS = ['chat', 'test', 'activate', 'disconnect'] as const;

export type AppIntegrationAction = (typeof APP_INTEGRATION_ACTIONS)[number];

export const hasSupportedAction = (item: AgentIntegrationOverviewItem, action: string) =>
  (item.supported_actions ?? item.provider?.supported_actions ?? []).includes(action);

export const shouldShowInAppIntegrationList = (item: AgentIntegrationOverviewItem) => {
  const actions = item.supported_actions ?? item.provider?.supported_actions;

  if (!actions || actions.length === 0) {
    return true;
  }

  return APP_INTEGRATION_ACTIONS.some((action) => actions.includes(action));
};

export const resolveProviderVisualKey = (item: AgentIntegrationOverviewItem) => {
  const candidates = [
    item.provider?.provider_id,
    item.provider?.display_name,
    item.provider?.provider_family,
  ]
    .filter(Boolean)
    .map((value) => slugify(value));

  for (const candidate of candidates) {
    if (PROVIDER_VISUALS[candidate]) {
      return candidate;
    }

    const alias = PROVIDER_ALIASES[candidate] ?? PROVIDER_ALIASES[candidate.replace(/-/g, '_')];
    if (alias && PROVIDER_VISUALS[alias]) {
      return alias;
    }
  }

  return candidates[0] ?? 'integration';
};

export const getProviderVisual = (item: AgentIntegrationOverviewItem): ProviderVisualConfig =>
  PROVIDER_VISUALS[resolveProviderVisualKey(item)] ?? {
    accentClassName: 'from-slate-500/15 via-zinc-500/10 to-stone-500/15',
  };

export const getIntegrationDescription = (
  item: AgentIntegrationOverviewItem,
  t: (key: string, options?: Record<string, unknown>) => string
) => {
  const provider = item.provider;
  const capabilities = provider?.supported_capabilities?.length
    ? provider.supported_capabilities
    : [];

  if (capabilities.length > 0) {
    const sentence = capabilities.slice(0, 3).join(', ');

    return (
      t('INTEGRATION_SUPPORTS', {
        name: provider.display_name,
        capabilities: sentence,
        defaultValue: '{{name}} supports {{capabilities}}.',
      }) || `${provider.display_name} supports ${sentence}.`
    );
  }

  if (provider?.provider_family) {
    const family = provider.provider_family.replace(/[_-]/g, ' ');

    return (
      t('INTEGRATION_BELONGS_TO_FAMILY', {
        name: provider.display_name,
        family,
        defaultValue: '{{name}} belongs to the {{family}} integration family.',
      }) || `${provider.display_name} belongs to the ${family} integration family.`
    );
  }

  const name = item.display_name ?? provider?.display_name ?? 'this channel';

  return (
    t('INTEGRATION_CONNECT_DESCRIPTION', {
      name,
      defaultValue: 'Connect {{name}} to use it as an external data source inside your workspace.',
    }) || `Connect ${name} to use it as an external data source inside your workspace.`
  );
};

export const hasExternalIntegrationData = (external?: Record<string, unknown> | null) =>
  Boolean(external && Object.keys(external).length > 0);

export const getOverviewChannelIds = (items: AgentIntegrationOverviewItem[] = []) => {
  const channelIds: string[] = [];
  const seen = new Set<string>();

  for (const item of items) {
    const channelId = item.provider?.provider_id?.trim();
    if (!channelId || seen.has(channelId)) {
      continue;
    }

    seen.add(channelId);
    channelIds.push(channelId);
  }

  return channelIds;
};

export type AgentIntegrationCardModel = {
  id: string;
  name: string;
  description: string;
  providerFamily: string;
  connected: boolean;
  isEnabled: boolean;
  canBrowse: boolean;
  resourceBrowseSupported: boolean;
  canChat: boolean;
  connectionUrl: string | null;
  connectionId: string | null;
  visual: ProviderVisualConfig;
  supportsConnect: boolean;
  supportsDisconnect: boolean;
  supportsChat: boolean;
  supportsSettings: boolean;
  supportsList: boolean;
  hasExternal: boolean;
  isMessaging: boolean;
};

export const mapOverviewItemToCard = (
  item: AgentIntegrationOverviewItem,
  t: (key: string, options?: Record<string, unknown>) => string
): AgentIntegrationCardModel => {
  const provider = item.provider ?? {
    provider_id: '',
    display_name: item.display_name ?? 'Integration',
    provider_family: '',
    auth_types: [],
    supported_capabilities: [],
    supported_scopes: [],
    status: item.status ?? '',
    resource_browse_supported: false,
  };
  const connected = isIntegrationConnected(item.status);
  const isMessaging = provider.provider_family === 'messaging';
  const hasExternal = hasExternalIntegrationData(item.external);

  return {
    id: provider.provider_id,
    name: item.display_name ?? provider.display_name,
    description: getIntegrationDescription(item, t),
    providerFamily: provider.provider_family,
    connected,
    isEnabled: provider.status === 'enabled' || provider.status === 'active',
    canBrowse: provider.resource_browse_supported && connected && !!item.connection_id,
    resourceBrowseSupported: provider.resource_browse_supported,
    canChat: item.can_chat,
    connectionUrl: item.connection_url,
    connectionId: item.connection_id,
    visual: getProviderVisual(item),
    supportsConnect: !connected && hasSupportedAction(item, 'activate'),
    supportsDisconnect: connected && hasSupportedAction(item, 'disconnect'),
    supportsChat: connected && hasSupportedAction(item, 'chat'),
    supportsSettings: provider.provider_id !== 'telegram' && connected && hasSupportedAction(item, 'test'),
    supportsList: isMessaging,
    hasExternal,
    isMessaging,
  };
};
